<template>
  <transition
    enter-from-class="tw-translate-x-full tw-opacity-0"
    enter-active-class="tw-transition tw-duration-300 tw-ease-out"
    leave-to-class="tw-translate-x-full tw-opacity-0"
    leave-active-class="tw-transition tw-duration-300 tw-ease-in"
  >
    <t-slide-out
      v-if="modelValue"
      variant="plain"
      :initial-width="600"
      test-id="connections-slide-out-2"
      @close="$emit('update:modelValue', false)"
    >
      <template #header>
        <div class="tw-flex tw-items-center">
          <div
            class="tw-mr-4 tw-p-2 tw-bg-primary-grey-50 tw-inline-flex tw-justify-center tw-items-center tw-rounded-lg tw-self-start"
          >
            <TClipboardIcon class="tw-w-6 tw-h-6" />
          </div>
          <p class="tw-text-xl tw-font-medium">
            {{ t("out-title-journaling-rule") }}
          </p>
        </div>
      </template>
      <template #body>
        <div class="tw-p-4 tw-bg-primary-grey-25 tw-h-full">
          <div class="tw-bg-system-warning-50 tw-p-4 tw-rounded-lg tw-mb-4">
            <div class="tw-flex tw-items-center tw-gap-2 tw-mb-4">
              <TAttentionIcon />
              <p>{{ t("manual-configuration-title") }}</p>
            </div>
            <p class="tw-text-sm">
              {{ t("manual-configuration-banner") }}
            </p>
          </div>
          <div>
            <p class="tw-text-xl tw-font-medium tw-mb-4">
              {{ t("journaling-manual-config") }}
            </p>
            <div class="tw-mb-4">
              <div class="tw-flex tw-items-center tw-mb-4">
                <span
                  class="tw-inline-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-shrink-0 tw-text-sm tw-font-medium tw-rounded-full tw-border tw-border-primary-grey-200 tw-bg-primary-grey-50 tw-mr-4"
                  >1</span
                >
                <p>{{ t("step-copy-journal-email") }}</p>
              </div>
              <div
                class="tw-border tw-border-primary-grey-100 tw-rounded-xl tw-p-4"
              >
                <div
                  class="tw-flex tw-items-center tw-bg-white tw-p-4 tw-mb-2 tw-rounded-xl tw-border tw-border-primary-grey-100"
                >
                  <span
                    class="tw-flex-1 tw-select-text tw-break-all tw-text-primary-grey-300"
                  >
                    {{ journalingRuleEmail }}
                  </span>
                  <t-copy-button
                    class="tw-ml-2"
                    :value="journalingRuleEmail"
                    :label="t('copy')"
                  />
                </div>
                <p class="tw-text-xs tw-mb-2">
                  Copy this email address to use in your M365 configuration
                </p>
              </div>
            </div>
            <div class="tw-mb-4">
              <div class="tw-flex tw-items-center tw-mb-4">
                <span
                  class="tw-inline-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-shrink-0 tw-text-sm tw-font-medium tw-rounded-full tw-border tw-border-primary-grey-200 tw-bg-primary-grey-50 tw-mr-4"
                  >2</span
                >
                <p>{{ t("step-go-to-admin-center") }}</p>
              </div>
              <div
                class="tw-border tw-border-primary-grey-100 tw-rounded-xl tw-p-4"
              >
                <t-link
                  :is-external="true"
                  :href="m365AdminCenterLink"
                  :string="t('open-admin-center')"
                  dataTestId="customer-settings-connections-ndr-settings-link"
                />
              </div>
            </div>
            <div class="tw-mb-4">
              <div class="tw-flex tw-items-center tw-mb-4">
                <span
                  class="tw-inline-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-shrink-0 tw-text-sm tw-font-medium tw-rounded-full tw-border tw-border-primary-grey-200 tw-bg-primary-grey-50 tw-mr-4"
                  >3</span
                >
                <p>{{ t("step-set-up-journaling-rule") }}</p>
              </div>
              <div
                class="tw-border tw-border-primary-grey-100 tw-rounded-xl tw-p-4"
              >
                <ul class="tw-list-decimal tw-pl-4">
                  <li class="tw-mb-2" v-html="t('select-new-rule')"></li>
                  <li class="tw-mb-2" v-html="t('paste-email-address')"></li>
                  <li class="tw-mb-2" v-html="t('select-everyone')"></li>
                  <li class="tw-mb-2" v-html="t('journal-rule-name')"></li>
                  <li class="tw-mb-2" v-html="t('next-submit')"></li>
                  <li class="tw-mb-2" v-html="t('confirm-test')"></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </template>
    </t-slide-out>
  </transition>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useAzureStore } from "@/store/azure.store";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import TSlideOut from "@/components/TSlideOut/TSlideOut.vue";
import TClipboardIcon from "@/components/TClipboardIcon/TClipboardIcon.vue";
import TCopyButton from "@/components/TCopyButton/TCopyButton.vue";
import TAttentionIcon from "@/components/TAttentionIcon/TAttentionIcon.vue";
import { getGraphInEmail } from "@/config/runtimeConfig";
import TLink from "@/components/TLink";

interface Props {
  modelValue: boolean;
}

defineProps<Props>();
defineEmits<{ "update:modelValue": [value: boolean] }>();

const azureStore = useAzureStore();
const { t } = useI18n();

const { azureAccounts } = storeToRefs(azureStore);
const m365AdminCenterLink = computed(
  () =>
    `https://purview.microsoft.com/datalifecyclemanagement/exchange/journalrules?tid=${azureAccounts.value[0]?.tenantID ?? ""}`,
);
const journalingRuleEmail = computed(
  () => `${azureAccounts.value?.[0]?.id ?? ""}${getGraphInEmail()}`,
);
</script>
